/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useClickOutside } from "../../hooks/useClickOutside";
import * as tokens from "../../design-tokens";
import { ModalHeader } from "./ModalHeader";
import { ModalFooter } from "./ModalFooter";
import type { ModalButtonProp, ModalType } from "./types";

const DEFAULT_TOP_POS = 60;

interface ModalProps extends React.ComponentPropsWithoutRef<"div"> {
  focusOnExit?: boolean;
  hideClose?: boolean;
  closeButtonTestId?: string;
  customModalWidth?: number;
  title?: string;
  subtitle?: string;
  primaryButton?: ModalButtonProp;
  secondaryButton?: ModalButtonProp;
  leftLink?: {
    text: string;
    href?: string;
  };
  type?: ModalType;
  hideIcon?: boolean;
  onClose?: () => void;
  top?: number;
}

const Modal = ({
  className = "",
  focusOnExit = true,
  title,
  hideClose = false,
  subtitle,
  primaryButton,
  secondaryButton,
  leftLink,
  children,
  hideIcon,
  onClose,
  closeButtonTestId,
  type = "info",
  top = DEFAULT_TOP_POS,
  customModalWidth = 640,
}: ModalProps) => {
  const [hide, setHide] = useState(false);
  const scrollPos = useMemo(() => window.scrollY, []);
  const lastFocus = useMemo<any>(() => document.activeElement, []);
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    setHide(true);
    if (onClose) {
      setTimeout(onClose, 200);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Escape") {
      handleClose();
    }
  };

  useClickOutside(modalContentRef, () => {
    if (hideClose) {
      return;
    }
    handleClose();
  });

  useEffect(() => {
    return () => {
      if (focusOnExit) {
        lastFocus.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (!hide) {
      modalContentRef?.current?.focus();
    }
  }, [hide, scrollPos]);

  const onKeyDownCallback = hideClose ? null : handleKeyDown;

  return (
    <ModalWrapper
      tabIndex={-1}
      hide={hide}
      role="dialog"
      aria-label="Opened modal"
      onKeyDown={onKeyDownCallback}
      className={`modal-wrapper ${className ?? ""}`}
    >
      <ModalBackground />
      {/* <FocusTrap> <--This is important for a11y and UX, but breaks any jsdom test that uses a modal */}
      <ModalContainer
        customModalWidth={customModalWidth}
        windowScrollPos={scrollPos}
        topPos={top}
        className="modal-container"
        ref={modalContentRef}
      >
        <ModalHeader
          type={type}
          hideIcon={hideIcon}
          title={title}
          hideClose={hideClose}
          subtitle={subtitle}
          onClose={handleClose}
          closeButtonTestId={closeButtonTestId}
        />

        <ModalContentContainer className="modal-content-container" type={type}>
          {children}
        </ModalContentContainer>
        <ModalFooter
          type={type}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          leftLink={leftLink}
        />
      </ModalContainer>
      {/* </FocusTrap> */}
    </ModalWrapper>
  );
};

const ModalContainer = styled.div<{
  windowScrollPos: number;
  topPos: number;
  customModalWidth: number;
}>`
  border: unset;
  background-color: var(--white);
  animation-name: srFadeIn;
  animation-duration: 0.2s;
  z-index: 1001;
  position: absolute;

  left: calc(50%);
  transform: translate(-50%);
  border-radius: 8px;
  border: 1px solid var(--tints-off-white-40);

  top: ${(props) => props.windowScrollPos}px;
  width: calc(100vw - 16px);
  @media screen and (min-width: 768px) {
    top: ${(props) => props.windowScrollPos + props.topPos}px;
    width: ${(props) => props.customModalWidth}px;
  }
`;

const ModalContentContainer = styled.div<{
  type: ModalType;
}>`
  padding: ${(props) =>
    props.type === "info" ? "0px 40px 24px 40px" : "24px 40px 24px 40px"};
  background-color: var(--white);
  color: ${tokens.TINTS_OFF_WHITE_20};
`;

const ModalWrapper = styled.div<{
  hide?: boolean;
}>`
  animation-name: ${(props) => (props.hide ? "srFadeOut" : undefined)};
  animation-duration: 0.23s;

  height: 0;
  width: 0;
  padding: 0;
  margin: 0;

  @keyframes srFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes srFadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  font-family: Roobert;
`;

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(33, 33, 33, 0.8);
  z-index: 1000;
  animation-name: srFadeIn;
  animation-duration: 0.2s;
`;

export { Modal };
export type { ModalProps };
